import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Check, Loader2, LogIn, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';
import usePushNotifications from '../hooks/usePushNotifications';
import { toast } from 'sonner';

const PREFERENCES = [
  { value: 'solicitacoes', label: 'Pedidos de Serviço', icon: '🤝', desc: 'Outros vizinhos pedindo orçamento' },
  { value: 'vagas',        label: 'Vagas de Emprego',  icon: '💼', desc: 'Empregos no município' },
  { value: 'promocoes',    label: 'Promoções',         icon: '🏷️', desc: 'Cupons e ofertas relâmpago' },
  { value: 'eventos',      label: 'Eventos',           icon: '📅', desc: 'O que vai acontecer perto' },
  { value: 'capacitacoes', label: 'Capacitações',      icon: '🎓', desc: 'Cursos e oficinas' },
  { value: 'novidades',    label: 'Novidades',         icon: '✨', desc: 'Lançamentos do município' },
];

export default function AlertSubscriptionModal({ isOpen, onClose, municipioId, municipioNome }) {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [selected, setSelected] = useState([]);
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const push = usePushNotifications();

  const fetchExisting = useCallback(async () => {
    if (!user || !municipioId) return;
    setLoadingExisting(true);
    try {
      const r = await api.get('/alerts/preferences/me');
      const mine = (r.data?.items || []).find(i => i.municipio_id === municipioId);
      if (mine) {
        setSelected(mine.preferencias || []);
        setActive(mine.ativo !== false);
      } else {
        setSelected([]);
        setActive(true);
      }
    } catch (e) { /* ignore */ }
    finally { setLoadingExisting(false); }
  }, [user, municipioId]);

  useEffect(() => { if (isOpen) fetchExisting(); }, [isOpen, fetchExisting]);

  const toggle = (pref) => {
    setSelected(s => s.includes(pref) ? s.filter(p => p !== pref) : [...s, pref]);
  };

  const handleSave = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    if (!municipioId) {
      toast.error('Selecione uma cidade primeiro para receber alertas locais.');
      return;
    }
    if (selected.length === 0) {
      toast.error('Escolha pelo menos uma preferência.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/alerts/preferences', {
        municipio_id: municipioId,
        preferencias: selected,
        ativo: active,
      });
      toast.success('Preferências salvas! Você verá os alertas no sino do topo.');
      onClose();
    } catch (e) {
      toast.error('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const handleDisable = async () => {
    if (!user || !municipioId) return;
    setLoading(true);
    try {
      await api.delete(`/alerts/preferences/${municipioId}`);
      toast.success('Alertas desativados para este município.');
      setActive(false);
      onClose();
    } catch (e) {
      toast.error('Erro: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="alert-subscription-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[#56007F]/10 rounded-lg">
              <Bell className="w-5 h-5 text-[#56007F]" />
            </div>
            <DialogTitle>Receber alertas no app</DialogTitle>
          </div>
          <DialogDescription>
            {user
              ? <>Os alertas aparecem direto no <strong>sino do topo</strong> da plataforma {municipioNome ? <>de <strong>{municipioNome}</strong></> : ''} — sem e-mail, sem WhatsApp.</>
              : <>Faça login para ativar alertas dentro da plataforma.</>}
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <div className="py-4 text-center space-y-3">
            <Sparkles className="w-10 h-10 mx-auto text-purple-500" />
            <p className="text-sm text-gray-600">
              Os alertas chegam no sino do app — só pra quem está logado.
            </p>
            <Button
              onClick={() => setShowAuth(true)}
              className="w-full bg-[#56007F] hover:bg-[#46006f]"
              data-testid="alert-login-cta"
            >
              <LogIn className="w-4 h-4 mr-2" /> Entrar para ativar
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-[300px] overflow-y-auto -mx-1 px-1">
              {PREFERENCES.map(p => {
                const checked = selected.includes(p.value);
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => toggle(p.value)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
                      checked ? 'border-[#56007F] bg-[#56007F]/5' : 'border-gray-200 hover:border-purple-300'
                    }`}
                    data-testid={`alert-pref-${p.value}`}
                  >
                    <span className="text-2xl">{p.icon}</span>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{p.label}</div>
                      <div className="text-xs text-gray-500">{p.desc}</div>
                    </div>
                    {checked && <Check className="w-5 h-5 text-[#56007F] shrink-0" />}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-3 border-t">
              <Badge variant="secondary" className="text-xs">
                {selected.length} {selected.length === 1 ? 'tipo selecionado' : 'tipos selecionados'}
              </Badge>
              {loadingExisting && <span className="text-xs text-gray-500"><Loader2 className="w-3 h-3 inline animate-spin" /> sincronizando…</span>}
            </div>

            {/* Notificações push no celular */}
            {push.isSupported && (
              <div className="p-3 rounded-lg border-2 border-gray-200 flex items-center gap-3">
                <span className="text-2xl">📱</span>
                <div className="flex-1">
                  <div className="font-medium text-sm">Notificações no celular</div>
                  <div className="text-xs text-gray-500">
                    {push.isSubscribed
                      ? 'Ativadas neste dispositivo'
                      : 'Receba os alertas como notificação, mesmo com o site fechado'}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={push.isSubscribed ? 'outline' : 'default'}
                  className={push.isSubscribed ? '' : 'bg-[#56007F] hover:bg-[#46006f]'}
                  disabled={push.loading || push.permission === 'denied'}
                  data-testid="alert-push-toggle"
                  onClick={async () => {
                    try {
                      if (push.isSubscribed) {
                        await push.unsubscribe();
                        toast.success('Notificações no celular desativadas');
                      } else {
                        const ok = await push.subscribe();
                        if (ok) toast.success('Notificações no celular ativadas! 📱');
                        else toast.error('Permissão de notificação negada pelo navegador');
                      }
                    } catch (e) {
                      toast.error('Não foi possível ativar as notificações');
                    }
                  }}
                >
                  {push.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (push.isSubscribed ? 'Desativar' : 'Ativar')}
                </Button>
              </div>
            )}
            {push.isSupported && push.permission === 'denied' && (
              <p className="text-xs text-amber-600">
                As notificações estão bloqueadas nas configurações do navegador para este site.
              </p>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleDisable}
                disabled={loading}
                className="flex-1"
                data-testid="alert-disable-button"
              >
                <BellOff className="w-4 h-4 mr-1" /> Pausar
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading || selected.length === 0}
                className="flex-1 bg-[#56007F] hover:bg-[#46006f]"
                data-testid="alert-save-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4 mr-1" /> Ativar</>}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        initialMode="login"
      />
    </Dialog>
  );
}

// Botão que abre o modal — usado no Hero da Home
export function AlertSubscriptionButton({ municipioId, municipioNome }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className="bg-white text-[#56007F] hover:bg-white/90 active:bg-white/95 font-bold rounded-full px-6 py-4 text-base shadow-lg hover:shadow-xl transition-all active:scale-95 border-2 border-white"
        data-testid="open-subscription-modal"
        aria-label="Abrir cadastro para receber alertas no app"
      >
        <Bell className="w-4 h-4 mr-2" aria-hidden="true" />
        Receber Alertas
      </Button>
      <AlertSubscriptionModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        municipioId={municipioId}
        municipioNome={municipioNome}
      />
    </>
  );
}
